import {
    TradingAccountFilterModel,
    TradingAccountReportModel,
    TradingAccountStatusesEnum,
    AccountChargeScheduleModel,
    FeeValidityEnum,
    AccountSubTypeEnum,
    TradingPlanTypeEnum, AccountPlanStatusEnum, FundOrderFilterModel
} from '@spa/api';

import {ColorCodesEnum, ComponentTypesEnum, FeatureEnum, UnspecifiedKeysEnum} from 'appShared/enums';
import {FilterControlInterface} from 'appShared/interfaces';
import {
    AmountControl,
    AutocompleteControl,
    BaseControl, CheckboxControl,
    DatepickerControl,
    Helper,
    SelectControl
} from '../../../app/shared/classes';
import {
    ReportTableData,
    ReportTableConfigInterface,
    ReportTableColumn,
    ReportTableActionsEnum, ReportTableButtonTypesEnum
} from '../../../app/shared/components';

import {TradingAccountTypesEnum} from 'appEntries/panel/enums';
import * as moment from 'moment';
import {environment} from 'appEnv/environment';

export const AccountClientIdSelector = 'clientId';
export const AccountClientTypeSelector = 'clientType';
export const AccountCashAccountIdSelector = 'cashAccountId';
export const AccountTypeSelector = 'accountType';
export const AccountReferenceIdSelector = 'referenceId';
export const AccountFilterBalanceSelector = 'accountFilterBalance';
export const AccountCreateDateFilterSelector = 'accountCreateDateFilterSelector';
export const AccountApprovedDateFilterSelector = 'accountApprovedDateFilterSelector';
export const AccountChargeScheduleFilterSelector = 'accountChargeScheduleFilterSelector';
export const AccountTradingPlanFilterSelector = 'accountTradingPlanFilterSelector';
export const PanelAccountsStorageId = 'PanelAccountsComponent';
export const AccountFeeCategorySelector = 'accountFeeCategorySelector';
export const AccountFeeGroupSelector = 'accountFeeGroupSelector';

/** The client mapping export covers margin and hard-BP trading accounts, and the front end is its only gate. */
export const MifirMappingExportSubTypes = (): number[] => [AccountSubTypeEnum.Trading, AccountSubTypeEnum.TradingHardBp];
export const IsMifirMappingExportAvailable = (type: unknown, subType: unknown): boolean =>
    type === TradingAccountTypesEnum.Trading && MifirMappingExportSubTypes().includes(subType as number);
export const MifirMappingExportUnavailableReason = (): string =>
    `Unavailable. Set Type to Trading and Sub Type to ${MifirMappingExportSubTypes()
        .map((s: number) => Helper.textsHelper().getAccountSubTypeName(s)).join(' or ')} to export the client mapping file.`;

const planColumnsSelectors = [
    TradingAccountReportModel.getTradingPlanReferenceIdSelector()
];

const closeoutPlanColumnsSelectors = [
    TradingAccountReportModel.getCloseoutPlanNameSelector()
];
export const PanelAccountsFilterControls = (): FilterControlInterface[] => {
    const controls = [];
    controls.push(
        {
            name: TradingAccountFilterModel.getAccountReferenceIdSelector(),
            placeholder: 'Account',
            controls: [
                new BaseControl({
                    name: TradingAccountFilterModel.getAccountReferenceIdSelector(),
                    value: null,
                    placeholder: 'Account',
                    validators: [],
                    componentType: ComponentTypesEnum.Text
                }),
            ],
            sortIndex: 1
        },
        {
            name: TradingAccountFilterModel.getAccountStatusSelector(),
            placeholder: 'Status',
            controls: [
                new SelectControl({
                    name: TradingAccountFilterModel.getAccountStatusSelector(),
                    value: TradingAccountStatusesEnum.Unspecified,
                    placeholder: 'Status',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.Unspecified),
                            value: TradingAccountStatusesEnum.Unspecified
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.Active),
                            value: TradingAccountStatusesEnum.Active
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.AwaitingFunding),
                            value: TradingAccountStatusesEnum.AwaitingFunding
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.PendingActivation),
                            value: TradingAccountStatusesEnum.PendingActivation
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.ForApproval),
                            value: TradingAccountStatusesEnum.ForApproval
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.Rejected),
                            value: TradingAccountStatusesEnum.Rejected
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.WithdrawalRestricted),
                            value: TradingAccountStatusesEnum.WithdrawalRestricted
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.Blocked),
                            value: TradingAccountStatusesEnum.Blocked
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.BlockOrderEntry),
                            value: TradingAccountStatusesEnum.BlockOrderEntry
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.BlockShortSells),
                            value: TradingAccountStatusesEnum.BlockShortSells
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.Closed),
                            value: TradingAccountStatusesEnum.Closed
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountStatusName(TradingAccountStatusesEnum.ClosedCancel),
                            value: TradingAccountStatusesEnum.ClosedCancel
                        },
                    ]
                })
            ],
            sortIndex: 2
        },
        {
            name: TradingAccountFilterModel.getIsCashAccountSelector(),
            placeholder: 'Type',
            controls: [
                new SelectControl({
                    name: TradingAccountFilterModel.getIsCashAccountSelector(),
                    value: TradingAccountTypesEnum.All,
                    placeholder: 'Type',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {
                            label: Helper.textsHelper().getTradingAccountTypeName(null),
                            value: TradingAccountTypesEnum.All
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountTypeName(false),
                            value: TradingAccountTypesEnum.Trading
                        },
                        {
                            label: Helper.textsHelper().getTradingAccountTypeName(true),
                            value: TradingAccountTypesEnum.Cash
                        },
                    ]
                })
            ],
            sortIndex: 3
        },
        {
            name: TradingAccountFilterModel.getAccountSubTypeSelector(),
            placeholder: 'Sub Type',
            controls: [
                new SelectControl({
                    name: TradingAccountFilterModel.getAccountSubTypeSelector(),
                    value: 0,
                    placeholder: 'Sub Type',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {
                            label: 'All',
                            value: 0
                        },
                        {
                            label: Helper.textsHelper().getAccountSubTypeName(AccountSubTypeEnum.Trading),
                            value: AccountSubTypeEnum.Trading
                        },
                        {
                            label: Helper.textsHelper().getAccountSubTypeName(AccountSubTypeEnum.TradingHardBp),
                            value: AccountSubTypeEnum.TradingHardBp
                        },
                        {
                            label: Helper.textsHelper().getAccountSubTypeName(AccountSubTypeEnum.MutualFund),
                            value: AccountSubTypeEnum.MutualFund
                        },
                        {
                            label: Helper.textsHelper().getAccountSubTypeName(AccountSubTypeEnum.InstrumentCertificate),
                            value: AccountSubTypeEnum.InstrumentCertificate
                        },
                    ]
                })
            ],
            sortIndex: 4
        },
        {
            name: TradingAccountFilterModel.getAccountTradingPlatformIdSelector(),
            placeholder: 'Platform',
            controls: [
                new SelectControl({
                    name: TradingAccountFilterModel.getAccountTradingPlatformIdSelector(),
                    value: null,
                    placeholder: 'Platform',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: []
                }),
            ],
            sortIndex: 5
        },
        {
            name: TradingAccountFilterModel.getOwnerUserNameSelector(),
            placeholder: 'Client',
            controls: [
                new BaseControl({
                    name: TradingAccountFilterModel.getOwnerUserNameSelector(),
                    value: null,
                    placeholder: 'Client',
                    validators: [],
                    componentType: ComponentTypesEnum.Text
                }),
            ],
            sortIndex: 6
        },
        {
            name: AccountFilterBalanceSelector,
            placeholder: 'Balance',
            controls: [
                new AmountControl({
                    name: TradingAccountFilterModel.getBalanceFromSelector(),
                    value: null,
                    placeholder: 'Balance from',
                    validators: [],
                    componentType: ComponentTypesEnum.AmountInput,
                    currency: null,
                    sortIndex: 1
                }),
                new AmountControl({
                    name: TradingAccountFilterModel.getBalanceToSelector(),
                    value: null,
                    placeholder: 'Balance to',
                    validators: [],
                    componentType: ComponentTypesEnum.AmountInput,
                    currency: null,
                    sortIndex: 2
                })
            ],
            sortIndex: 7
        },
        {
            name: TradingAccountFilterModel.getCurrencySelector(),
            placeholder: 'Currency',
            controls: [
                new SelectControl({
                    name: TradingAccountFilterModel.getCurrencySelector(),
                    value: null,
                    placeholder: 'Currency',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: []
                }),
            ],
            sortIndex: 8
        },
        {
            name: AccountCreateDateFilterSelector,
            placeholder: 'Created Date',
            controls: [
                new DatepickerControl({
                    name: TradingAccountFilterModel.getAccountCreatedDateFromSelector(),
                    value: null,
                    placeholder: 'Created Date From',
                    maxDate: moment().format('YYYY-MM-DD'),
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 1
                }),
                new DatepickerControl({
                    name: TradingAccountFilterModel.getAccountCreatedDateToSelector(),
                    value: null,
                    placeholder: 'Created Date To',
                    maxDate: moment().format('YYYY-MM-DD'),
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 2
                }),
            ],
            shown: true,
            sortIndex: 9
        },
        {
            name: AccountApprovedDateFilterSelector,
            placeholder: 'Approved Date',
            controls: [
                new DatepickerControl({
                    name: TradingAccountFilterModel.getAccountApprovedDateFromSelector(),
                    value: null,
                    placeholder: 'Approved Date From',
                    maxDate: moment().format('YYYY-MM-DD'),
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 1
                }),
                new DatepickerControl({
                    name: TradingAccountFilterModel.getAccountApprovedDateToSelector(),
                    value: null,
                    placeholder: 'Approved Date To',
                    maxDate: moment().format('YYYY-MM-DD'),
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 2
                }),
            ],
            shown: true,
            sortIndex: 10
        },
        {
            name: TradingAccountFilterModel.getGrouReferenceIdSelector(),
            placeholder: 'Group',
            controls: [
                new AutocompleteControl({
                    name: TradingAccountFilterModel.getGrouReferenceIdSelector(),
                    value: null,
                    placeholder: 'Group',
                    validators: [],
                    componentType: ComponentTypesEnum.Autocomplete,
                    options: []
                }),
            ],
            sortIndex: 11
        },
        {
            name: AccountChargeScheduleFilterSelector,
            placeholder: 'Fee Related Filters',
            block: true,
            controls: [
                new SelectControl({
                    name: AccountFeeCategorySelector,
                    value: null,
                    placeholder: 'Fee Category',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    sortIndex: 1,
                    options: []
                }),
                new SelectControl({
                    name: AccountFeeGroupSelector,
                    value: null,
                    placeholder: 'Fee Plan',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    sortIndex: 2,
                    options: []
                }),
                new SelectControl({
                    name: TradingAccountFilterModel.getFeeValiditySelector(),
                    value: FeeValidityEnum.Assigned,
                    placeholder: 'Fee Availability',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    sortIndex: 3,
                    options: [
                        {
                            label: Helper.textsHelper().getFeeValidityName(FeeValidityEnum.Assigned),
                            value: FeeValidityEnum.Assigned
                        },
                        {
                            label: Helper.textsHelper().getFeeValidityName(FeeValidityEnum.AssignedAndValid),
                            value: FeeValidityEnum.AssignedAndValid
                        },
                        {
                            label: Helper.textsHelper().getFeeValidityName(FeeValidityEnum.AssignedAndExpired),
                            value: FeeValidityEnum.AssignedAndExpired
                        },
                        {
                            label: Helper.textsHelper().getFeeValidityName(FeeValidityEnum.Missing),
                            value: FeeValidityEnum.Missing
                        }]
                }),
                new SelectControl({
                    name: TradingAccountFilterModel.getChargeScheduleReferenceIdSelector(),
                    value: null,
                    placeholder: 'Fee',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    sortIndex: 4,
                    options: []
                }),
                new DatepickerControl({
                    name: TradingAccountFilterModel.getChargeScheduleStartDateSelector(),
                    value: null,
                    placeholder: 'Valid From',
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 5,
                    resetable: true
                }),
                new DatepickerControl({
                    name: TradingAccountFilterModel.getChargeScheduleEndDateSelector(),
                    value: null,
                    placeholder: 'Valid To',
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 6,
                    resetable: true
                }),
            ],
            shown: false,
            sortIndex: 100
        },
        {
            name: TradingAccountFilterModel.getIncludeAccessedUsersSelector(),
            placeholder: 'Show Traders',
            controls: [
                new CheckboxControl({
                    name: TradingAccountFilterModel.getIncludeAccessedUsersSelector(),
                    value: false,
                    placeholder: 'Show Traders',
                    validators: [],
                    componentType: ComponentTypesEnum.Checkbox,
                    floatingLabelNever: true,
                    checked: false,
                    color: ColorCodesEnum.Primary,
                    expectBooleanValue: true
                })
            ],
            sortIndex: 101,
            shown: true,
        },
    );
    if (Helper.isFeatureEnabled(FeatureEnum.CloseoutPlans)) {
        controls.push({
            name: TradingAccountFilterModel.getCloseoutPlanIdSelector(),
            placeholder: 'Closeout Plan',
            controls: [
                new AutocompleteControl({
                    name: TradingAccountFilterModel.getCloseoutPlanIdSelector(),
                    value: null,
                    placeholder: 'Closeout Plan',
                    validators: [],
                    componentType: ComponentTypesEnum.Autocomplete,
                    options: []
                }),
            ],
            sortIndex: 12
        });
    }
    if (Helper.isFeatureEnabled(FeatureEnum.TradingPlans)) {
        controls.push({
            name: AccountTradingPlanFilterSelector,
            placeholder: 'Trading Plan Related Filters',
            block: true,
            controls: [
                new BaseControl({
                    name: TradingAccountFilterModel.getTradingPlanNameSelector(),
                    value: null,
                    placeholder: 'Plan Name',
                    validators: [],
                    componentType: ComponentTypesEnum.Text,
                    sortIndex: 1,
                }),
                new SelectControl({
                    name: TradingAccountFilterModel.getTradingPlanTypeEnumSelector(),
                    value: UnspecifiedKeysEnum.UnspecifiedKey,
                    placeholder: 'Plan Type',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    sortIndex: 2,
                    options: [
                        {
                            label: 'All',
                            value: UnspecifiedKeysEnum.UnspecifiedKey
                        },
                        {
                            label: Helper.textsHelper().getTradingPlanTypeName(TradingPlanTypeEnum.Demo),
                            value: TradingPlanTypeEnum.Demo
                        },
                        {
                            label: Helper.textsHelper().getTradingPlanTypeName(TradingPlanTypeEnum.Tryout),
                            value: TradingPlanTypeEnum.Tryout
                        },
                        {
                            label: Helper.textsHelper().getTradingPlanTypeName(TradingPlanTypeEnum.Tournament),
                            value: TradingPlanTypeEnum.Tournament
                        },
                        {
                            label: Helper.textsHelper().getTradingPlanTypeName(TradingPlanTypeEnum.PrimeTrading),
                            value: TradingPlanTypeEnum.PrimeTrading
                        },
                        {
                            label: Helper.textsHelper().getTradingPlanTypeName(TradingPlanTypeEnum.FundedAccount),
                            value: TradingPlanTypeEnum.FundedAccount
                        }]
                }),
                new BaseControl({
                    name: TradingAccountFilterModel.getTradingPlanPhaseSelector(),
                    value: null,
                    placeholder: 'Plan Phase',
                    validators: [],
                    componentType: ComponentTypesEnum.Text,
                    sortIndex: 3,
                }),
                new SelectControl({
                    name: TradingAccountFilterModel.getAccountTradingPlanStatusEnumSelector(),
                    value: UnspecifiedKeysEnum.UnspecifiedKey,
                    placeholder: 'Account Plan Status',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    sortIndex: 4,
                    options: [
                        {
                            label: 'All',
                            value: UnspecifiedKeysEnum.UnspecifiedKey
                        },
                        {
                            label: Helper.textsHelper().getAccountPlanStatusName(AccountPlanStatusEnum.InProgress),
                            value: AccountPlanStatusEnum.InProgress
                        },
                        {
                            label: Helper.textsHelper().getAccountPlanStatusName(AccountPlanStatusEnum.Successful),
                            value: AccountPlanStatusEnum.Successful
                        },
                        {
                            label: Helper.textsHelper().getAccountPlanStatusName(AccountPlanStatusEnum.Failed),
                            value: AccountPlanStatusEnum.Failed
                        },
                        {
                            label: Helper.textsHelper().getAccountPlanStatusName(AccountPlanStatusEnum.Rejected),
                            value: AccountPlanStatusEnum.Rejected
                        }]
                }),
                new AmountControl({
                    name: TradingAccountFilterModel.getTradingPlanBpEqAmountFromSelector(),
                    value: null,
                    placeholder: 'BP/EQ From',
                    validators: [],
                    componentType: ComponentTypesEnum.AmountInput,
                    currency: null,
                    sortIndex: 5,
                }),
                new AmountControl({
                    name: TradingAccountFilterModel.getTradingPlanBpEqAmountToSelector(),
                    value: null,
                    placeholder: 'BP/EQ To',
                    validators: [],
                    componentType: ComponentTypesEnum.AmountInput,
                    currency: null,
                    sortIndex: 6,
                }),
                new DatepickerControl({
                    name: TradingAccountFilterModel.getTradingPlanCreatedOnFromSelector(),
                    value: null,
                    placeholder: 'Created Date From',
                    maxDate: moment().format(environment.dateFormatLocalDate),
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 7,
                }),
                new DatepickerControl({
                    name: TradingAccountFilterModel.getTradingPlanCreatedOnToSelector(),
                    value: null,
                    placeholder: 'Created Date To',
                    maxDate: moment().format(environment.dateFormatLocalDate),
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 8,
                })
            ],
            shown: false,
            sortIndex: 101
        });
    }

    return controls;
};

export const PanelAccountsDataTableItems = (): ReportTableData[] => {
    const columns = [
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountReferenceIdSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountStatusNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountTypeNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountTradingPlatformNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getOwnerUserNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccessedUserFirstNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccessedUserLastNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: TradingAccountReportModel.getBalanceSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: TradingAccountReportModel.getCurrencyCodeSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountCreatedDateSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountApprovedDateSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: TradingAccountReportModel.getOwnerClientIdSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: TradingAccountReportModel.getOwnerOldClientIdSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getCreatedByUserNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getApprovedByUserNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getFirstTradeDateSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getLastTradeDateSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getTradingPlanReferenceIdSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getExecutionBrokerNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getDefaultClearingAccountRefId(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getCloseoutPlanNameSelector(),
            value: null,
            hidden: false
        }),
    ];
    if (!Helper.isFeatureEnabled(FeatureEnum.TradingPlans) || !Helper.isFeatureEnabled(FeatureEnum.CloseoutPlans)) {
        const removedSelectors = [
            ...(Helper.isFeatureEnabled(FeatureEnum.TradingPlans) ? [] : planColumnsSelectors),
            ...(Helper.isFeatureEnabled(FeatureEnum.CloseoutPlans) ? [] : closeoutPlanColumnsSelectors)
        ];

        return columns.filter(column => !removedSelectors.includes(column.colId));
    }

    return columns;
};

export const PanelAccountsDataTableConfig = (): ReportTableConfigInterface => {

    let columns = [
        new ReportTableColumn({
            label: 'Account',
            selector: TradingAccountReportModel.getAccountReferenceIdSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Status',
            selector: TradingAccountReportModel.getAccountStatusNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Type',
            selector: TradingAccountReportModel.getAccountTypeNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Platform',
            selector: TradingAccountReportModel.getAccountTradingPlatformNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Client',
            selector: TradingAccountReportModel.getOwnerUserNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'First Name',
            selector: TradingAccountReportModel.getAccessedUserFirstNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Last Name',
            selector: TradingAccountReportModel.getAccessedUserLastNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Balance',
            selector: TradingAccountReportModel.getBalanceSelector()
        }),
        new ReportTableColumn({
            label: 'Currency',
            selector: TradingAccountReportModel.getCurrencyCodeSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Created On',
            selector: TradingAccountReportModel.getAccountCreatedDateSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Approved On',
            selector: TradingAccountReportModel.getAccountApprovedDateSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Client Id',
            selector: TradingAccountReportModel.getOwnerClientIdSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Old Client Id',
            selector: TradingAccountReportModel.getOwnerOldClientIdSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Created By',
            selector: TradingAccountReportModel.getCreatedByUserNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Approved By',
            selector: TradingAccountReportModel.getApprovedByUserNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'First Trade',
            selector: TradingAccountReportModel.getFirstTradeDateSelector()
        }),
        new ReportTableColumn({
            label: 'Last Trade',
            selector: TradingAccountReportModel.getLastTradeDateSelector()
        }),
        new ReportTableColumn({
            label: 'Plan',
            selector: TradingAccountReportModel.getTradingPlanReferenceIdSelector()
        }),
        new ReportTableColumn({
            label: 'Executing broker',
            selector: TradingAccountReportModel.getExecutionBrokerNameSelector(),
            sortable: false
        }),
        new ReportTableColumn({
            label: 'Clearing Account Ref Id',
            selector: TradingAccountReportModel.getDefaultClearingAccountRefId()
        }),
        new ReportTableColumn({
            label: 'Closeout Plans',
            selector: TradingAccountReportModel.getCloseoutPlanNameSelector(),
            sortable: true
        }),
    ];
    const hiddenColumnIds = [TradingAccountReportModel.getExecutionBrokerNameSelector()];
    if (Helper.isFeatureEnabled(FeatureEnum.CloseoutPlans)) {
        hiddenColumnIds.push(TradingAccountReportModel.getCloseoutPlanNameSelector());
    } else {
        columns = columns.filter(column => !closeoutPlanColumnsSelectors.includes(column.selector));
    }
    if (!Helper.isFeatureEnabled(FeatureEnum.TradingPlans)) {
        columns = columns.filter(column => !planColumnsSelectors.includes(column.selector));
    }

    return {
        rows: [],
        columns,
        hiddenColumnIds,
        actionMenu: false,
        hideHiddenColumnsOnSelection:  true,
    };
};


export const PanelAccountsPlanDataTableItems = (): ReportTableData[] => {
    const columns = [
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountReferenceIdSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountStatusNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountTypeNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getOwnerUserNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: TradingAccountReportModel.getTradingPlanNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: TradingAccountReportModel.getTradingPlanTypeNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getTradingPlanPhaseSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountTradingPlanStatusNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: TradingAccountReportModel.getBpeqSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountCreatedDateSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getCreatedByUserNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getCloseoutPlanNameSelector(),
            value: null,
            hidden: false
        })
    ];
    if (!Helper.isFeatureEnabled(FeatureEnum.CloseoutPlans)) {
        return columns.filter(column => !closeoutPlanColumnsSelectors.includes(column.colId));
    }

    return columns;
};

export const PanelAccountsPlanDataTableConfig = (): ReportTableConfigInterface => {

    let columns = [
        new ReportTableColumn({
            label: 'Account',
            selector: TradingAccountReportModel.getAccountReferenceIdSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Status',
            selector: TradingAccountReportModel.getAccountStatusNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Type',
            selector: TradingAccountReportModel.getAccountTypeNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Client',
            selector: TradingAccountReportModel.getOwnerUserNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Plan',
            selector: TradingAccountReportModel.getTradingPlanNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Plan Type',
            selector: TradingAccountReportModel.getTradingPlanTypeNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Plan Phase',
            selector: TradingAccountReportModel.getTradingPlanPhaseSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Acc Pl Status',
            selector: TradingAccountReportModel.getAccountTradingPlanStatusNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Bp / Eq',
            selector: TradingAccountReportModel.getBpeqSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Created On',
            selector: TradingAccountReportModel.getAccountCreatedDateSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Created By',
            selector: TradingAccountReportModel.getCreatedByUserNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Closeout Plans',
            selector: TradingAccountReportModel.getCloseoutPlanNameSelector(),
            sortable: true
        })
    ];
    const hiddenColumnIds = [TradingAccountReportModel.getExecutionBrokerNameSelector()];
    if (Helper.isFeatureEnabled(FeatureEnum.CloseoutPlans)) {
        hiddenColumnIds.push(TradingAccountReportModel.getCloseoutPlanNameSelector());
    } else {
        columns = columns.filter(column => !closeoutPlanColumnsSelectors.includes(column.selector));
    }
    if (!Helper.isFeatureEnabled(FeatureEnum.TradingPlans)) {
        columns = columns.filter(column => !planColumnsSelectors.includes(column.selector));
    }

    return {
        rows: [],
        columns,
        hiddenColumnIds,
        actionMenu: false,
        hideHiddenColumnsOnSelection:  true,
    };
};

export const PanelAccountsWithChargeSchedulesDataTableItems = (): ReportTableData[] => {
    const columns = [
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountReferenceIdSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountStatusNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountTypeNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getAccountTradingPlatformNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: TradingAccountReportModel.getOwnerUserNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: AccountChargeScheduleModel.getChargeGroupCodeSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: TradingAccountFilterModel.getFeeValiditySelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: TradingAccountFilterModel.getChargeScheduleReferenceIdSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: AccountChargeScheduleModel.getStartDateSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: AccountChargeScheduleModel.getEndDateSelector(),
            value: null,
            hidden: false
        })
    ];

    return columns;
};

export const PanelAccountsWithChargeSchedulesDataTableConfig = (): ReportTableConfigInterface => {

    const columns = [
        new ReportTableColumn({
            label: 'Account',
            selector: TradingAccountReportModel.getAccountReferenceIdSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Status',
            selector: TradingAccountReportModel.getAccountStatusNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Type',
            selector: TradingAccountReportModel.getAccountTypeNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Platform',
            selector: TradingAccountReportModel.getAccountTradingPlatformNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Client',
            selector: TradingAccountReportModel.getOwnerUserNameSelector(),
            sortable: true
        }),
        new ReportTableColumn({
            label: 'Group',
            selector: AccountChargeScheduleModel.getChargeGroupCodeSelector()
        }),
        new ReportTableColumn({
            label: 'Fee Availability',
            selector: TradingAccountFilterModel.getFeeValiditySelector()
        }),
        new ReportTableColumn({
            label: 'Fee',
            selector: TradingAccountFilterModel.getChargeScheduleReferenceIdSelector()
        }),
        new ReportTableColumn({
            label: 'Valid From',
            selector: AccountChargeScheduleModel.getStartDateSelector()
        }),
        new ReportTableColumn({
            label: 'Valid To',
            selector: AccountChargeScheduleModel.getEndDateSelector()
        }),
    ];

    return {
        rows: [],
        columns,
        hiddenColumnIds: [],
        actionMenu: false,
        hideHiddenColumnsOnSelection:  true,
        hasToggleableOrCheckableRows: true,
        hasHeaderCheckbox: true,
        actionMenuItems: [
            {
                linkText: 'Add fee plan',
                action: ReportTableActionsEnum.Add,
                buttonType: ReportTableButtonTypesEnum.Add
            }
        ]
    };
};
